'use client';

import { getDictionary } from '@/app/dictionaries/getDictionary';
import { LogOut } from 'lucide-react';
import { useLocale } from 'next-intl';
import { useEffect, useState } from 'react';

/**
 * The exit from an impersonation: a thin handle on the left edge that opens into a dark
 * button on hover or keyboard focus. It carries no name; the sidebar already shows whose
 * view this is. The label sits outside the button's box so that, hidden, it covers nothing.
 */
export function ImpersonationHandle({ onExit }: { onExit: () => Promise<boolean> }) {
  const locale = useLocale();
  const [label, setLabel] = useState('');
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    let live = true;
    getDictionary(locale).then((dictionary: any) => {
      if (live) setLabel(dictionary?.auth?.impersonation?.exit ?? '');
    });
    return () => { live = false; };
  }, [locale]);

  const exit = async () => {
    if (exiting) return;
    setExiting(true);
    // A full load, not a client navigation: every cache on the page holds the agent's data.
    if (await onExit()) window.location.assign(`/${locale}/admin/valuation-report-builder`);
    else setExiting(false);
  };

  return (
    <button
      type="button"
      onClick={exit}
      disabled={exiting}
      aria-label={label}
      className="group fixed left-1.5 top-1/2 z-[9999] flex -translate-y-1/2 items-center py-2 pr-1 focus:outline-none"
    >
      <span className="flex h-11 w-1.5 items-center justify-center rounded-full bg-gray-400 shadow-sm transition-all duration-150 group-hover:h-14 group-hover:w-10 group-hover:rounded-xl group-hover:bg-gray-900 group-focus-visible:h-14 group-focus-visible:w-10 group-focus-visible:rounded-xl group-focus-visible:bg-gray-900">
        <LogOut className="h-4 w-4 text-white opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-visible:opacity-100" />
      </span>
      <span className="pointer-events-none invisible absolute left-full top-1/2 ml-2 -translate-x-1 -translate-y-1/2 whitespace-nowrap rounded-full bg-black px-3 py-1.5 text-xs font-medium text-white opacity-0 transition-all duration-150 group-hover:visible group-hover:translate-x-0 group-hover:opacity-100 group-focus-visible:visible group-focus-visible:translate-x-0 group-focus-visible:opacity-100">
        {label}
      </span>
    </button>
  );
}
